import React from "react";
import "../../styles/ExistingClaimForm.scss";
import ClaimFormSchema from "../../schemas/ClaimFormSchema";
import SearchInput from "../search-input/SearchInput";
import HelperText from "../helper-text/HelperText";

const ExistingClaimForm = (props) => {
    const { claimFormRef: { register, setValue, formState, getValues, clearErrors }, serviceNumber, currentUser } = props;
    const { lastName } = ClaimFormSchema;
    // PATU-68 (decision D1 / Reading B): lock the last-name field for ANY authenticated
    // non-lender (federated Contract Holder) session, even when the JWT carried no lastName
    // claim — it then renders locked-and-empty. Mirrors ContractForm. Lenders stay editable.
    const isLastNameLocked = !!(currentUser?.authenticated && !currentUser?.isLender);

    React.useEffect(() => {
        // PATU-68: only prefill when the JWT actually carried a last name; otherwise the field
        // stays locked-and-empty and we must not dereference a null lastName.
        if (isLastNameLocked && currentUser?.lastName) {
            // PATU-75: mirror PATU-70 on the claim-lookup form. The field is
            // locked, so the holder cannot shorten an over-length JWT last name.
            // The lookup matches on a 30-char case-insensitive prefix, so truncate
            // to the field's 30-char max before validating — otherwise the
            // untruncated value fails the maxLength rule and the Find button stays
            // disabled, locking the holder out of their own claim.
            setValue('lastName', currentUser.lastName.toUpperCase().slice(0, 30), { shouldValidate: true });
        }
    }, [isLastNameLocked, currentUser?.lastName, setValue])

    const handleSetValue = (e) => {
        e.preventDefault();
        setValue(e.target.name, e.target.value.trim().toUpperCase(), { shouldValidate: true })
    }

    const formatPhoneNumber = (phoneNumberString) => {
        let cleaned = ('' + phoneNumberString).replace(/\D/g, '');
        const match = cleaned.match(/^(\d{1,3})(\d{0,3})(\d{0,4})(\d{0,4})$/);
        if (match) {
            cleaned = `(${match[1]}${match[2] ? ') ' : ''}${match[2]}${match[3] ? '-' : ''}${match[3]}${match[4] ? ' x' : ''}${match[4]}`;
        }
        return cleaned;
    }

    return (
        <React.Fragment>
            <div className="contract-heading">Locate Your Claim</div>
            <p className="contract-description">
                Check the status of your claim, or update your claim with any new documents you have.
            </p>
            <div className="mt-12">
                <SearchInput
                    label="Last Name"
                    name="lastName"
                    isRequired={true}
                    maxLength={30}
                    fieldRegister={register('lastName', {
                        ...lastName,
                        onChange: (e) => { setValue(e.target.name, e.target.value.toUpperCase(), { shouldValidate: true }) }
                    })}
                    errorMessage={formState?.errors?.lastName?.message || null}
                    {...props}
                    disabled={isLastNameLocked}
                />
                <HelperText text="The last name that appears on your claim." isShownHelperIcon={false} />
            </div>
            <div className="mt-12">
                <h6 className="and-text"><span>and either</span></h6>
            </div>
            <div className="mt-12">
                <SearchInput
                    label="Claim Number"
                    name="claimNumber"
                    maxLength={25}
                    fieldRegister={register('claimNumber', {
                        validate: {
                            required: (value) => {
                                if (!value && !getValues('contractNumber')) {
                                    clearErrors('contractNumber');
                                    clearErrors('claimNumber');
                                    return 'Claim Number or Contract Number is required.';
                                }
                                return true;
                            },
                        },
                        onChange: (e) => { handleSetValue(e) }
                    })}
                    errorMessage={(!getValues('contractNumber') && formState?.errors?.claimNumber?.message) ? formState?.errors?.claimNumber?.message : null}
                    {...props}
                />
                <HelperText text="Enter the claim number assigned to you." isShownHelperIcon={false} />
            </div>
            <div className="mt-12">
                <h6 className="and-text"><span>or</span></h6>
            </div>
            <div className="mt-12">
                <SearchInput
                    label="Contract Number"
                    name="contractNumber"
                    isRequired={false}
                    maxLength={25}
                    fieldRegister={register('contractNumber', {
                        validate: {
                            required: (value) => {
                                if (!value && !getValues('claimNumber')) {
                                    clearErrors('contractNumber');
                                    clearErrors('claimNumber');
                                    return 'Contract Number or Claim Number is required.';
                                }
                                return true;
                            },
                        },
                        onChange: (e) => { handleSetValue(e) }
                    })}
                    errorMessage={(!getValues('claimNumber') && formState?.errors?.contractNumber?.message) ? formState?.errors?.contractNumber?.message : null}
                    {...props}
                />
                <HelperText text={`As found in your paperwork or call us at ` + formatPhoneNumber(serviceNumber) + `.`} isShownHelperIcon={false} />
            </div>
        </React.Fragment>
    )
};

export default ExistingClaimForm;